import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "../shared/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Monitor,
  Plus,
  Edit,
  Trash2,
  Download,
  Upload,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  getDesktopVersions,
  uploadDesktopVersion,
  updateDesktopVersion,
  deleteDesktopVersion,
  getDesktopStats,
} from "../../../data";

export function DesktopVersionsListScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [uploadFormData, setUploadFormData] = useState({
    version: "",
    platform: "windows",
    fileType: "installer",
    releaseNotes: "",
    changelog: [],
    isLatest: false,
    minOsVersion: "",
  });

  const [editFormData, setEditFormData] = useState({
    releaseNotes: "",
    changelog: [],
    isLatest: false,
    isActive: true,
    minOsVersion: "",
  });

  const [newChangelogItem, setNewChangelogItem] = useState("");

  // Queries
  const { data: versionsData, isLoading } = useQuery({
    queryKey: ["desktop-versions"],
    queryFn: getDesktopVersions,
  });

  const { data: statsData } = useQuery({
    queryKey: ["desktop-stats"],
    queryFn: getDesktopStats,
  });

  const versions = versionsData?.data || [];
  const stats = statsData?.data || { byPlatform: [], total: 0 };

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: uploadDesktopVersion,
    onSuccess: () => {
      queryClient.invalidateQueries(["desktop-versions"]);
      queryClient.invalidateQueries(["desktop-stats"]);
      toast({
        title: "Başarılı",
        description: "Versiyon başarıyla yüklendi",
      });
      handleCloseUploadDialog();
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Versiyon yüklenemedi",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateDesktopVersion(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["desktop-versions"]);
      toast({
        title: "Başarılı",
        description: "Versiyon başarıyla güncellendi",
      });
      handleCloseEditDialog();
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Versiyon güncellenemedi",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDesktopVersion,
    onSuccess: () => {
      queryClient.invalidateQueries(["desktop-versions"]);
      queryClient.invalidateQueries(["desktop-stats"]);
      toast({
        title: "Başarılı",
        description: "Versiyon başarıyla silindi",
      });
      handleCloseDeleteDialog();
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Versiyon silinemedi",
        variant: "destructive",
      });
    },
  });

  // Handlers
  const handleOpenUploadDialog = () => {
    setUploadFormData({
      version: "",
      platform: "windows",
      fileType: "portable",
      releaseNotes: "",
      changelog: [],
      isLatest: false,
      minOsVersion: "",
    });
    setSelectedFile(null);
    setNewChangelogItem("");
    setIsUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setIsUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadFormData({
      version: "",
      platform: "windows",
      fileType: "portable",
      releaseNotes: "",
      changelog: [],
      isLatest: false,
      minOsVersion: "",
    });
    setNewChangelogItem("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenEditDialog = (version) => {
    setSelectedVersion(version);
    setEditFormData({
      releaseNotes: version.releaseNotes || "",
      changelog: version.changelog || [],
      isLatest: version.isLatest || false,
      isActive: version.isActive !== undefined ? version.isActive : true,
      minOsVersion: version.minOsVersion || "",
    });
    setNewChangelogItem("");
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedVersion(null);
  };

  const handleOpenDeleteDialog = (version) => {
    setSelectedVersion(version);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedVersion(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleAddChangelogItem = (isUpload = true) => {
    if (!newChangelogItem.trim()) return;

    if (isUpload) {
      setUploadFormData({
        ...uploadFormData,
        changelog: [...uploadFormData.changelog, newChangelogItem.trim()],
      });
    } else {
      setEditFormData({
        ...editFormData,
        changelog: [...editFormData.changelog, newChangelogItem.trim()],
      });
    }
    setNewChangelogItem("");
  };

  const handleRemoveChangelogItem = (index, isUpload = true) => {
    if (isUpload) {
      setUploadFormData({
        ...uploadFormData,
        changelog: uploadFormData.changelog.filter((_, i) => i !== index),
      });
    } else {
      setEditFormData({
        ...editFormData,
        changelog: editFormData.changelog.filter((_, i) => i !== index),
      });
    }
  };

  const handleUploadSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast({
        title: "Hata",
        description: "Lütfen bir dosya seçin",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("version", uploadFormData.version);
    formData.append("platform", uploadFormData.platform);
    formData.append("fileType", uploadFormData.fileType);
    formData.append("releaseNotes", uploadFormData.releaseNotes);
    formData.append("changelog", JSON.stringify(uploadFormData.changelog));
    formData.append("isLatest", uploadFormData.isLatest);
    formData.append("minOsVersion", uploadFormData.minOsVersion);

    uploadMutation.mutate(formData);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      id: selectedVersion.id,
      data: editFormData,
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(selectedVersion.id);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileTypeOptions = () => {
    // Always return Windows options since we only support Windows
    return [
      { value: "installer", label: "Installer" },
      { value: "portable", label: "Portable" },
    ];
  };

  return (
    <AdminLayout
      title="Desktop App Versions"
      description="EyayBooth masaüstü uygulaması versiyonlarını yönetin"
      loading={isLoading}
    >
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Downloads
            </CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Windows Downloads
            </CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.byPlatform?.find((p) => p.platform === "windows")?._sum
                ?.downloadCount || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Versions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Monitor className="mr-2 h-5 w-5" />
              Versions ({versions.length})
            </CardTitle>
            <Button onClick={handleOpenUploadDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Versiyon Yükle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Monitor className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz versiyon bulunmuyor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>File Type</TableHead>
                  <TableHead>File Size</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Latest</TableHead>
                  <TableHead>Aktif</TableHead>
                  <TableHead>Release Date</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell className="font-medium">
                      {version.version}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        <Monitor className="mr-1 h-3 w-3" />
                        {version.platform}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{version.fileType}</Badge>
                    </TableCell>
                    <TableCell>
                      {formatFileSize(Number(version.fileSize))}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Download className="mr-1 h-3 w-3 text-gray-400" />
                        {version.downloadCount || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      {version.isLatest ? (
                        <Badge variant="default">Latest</Badge>
                      ) : (
                        <Badge variant="outline">—</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {version.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(version.releaseDate).toLocaleDateString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(version)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDeleteDialog(version)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleUploadSubmit}>
            <DialogHeader>
              <DialogTitle>Yeni Desktop App Versiyonu Yükle</DialogTitle>
              <DialogDescription>
                Masaüstü uygulama installer dosyasını yükleyin
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="version">Version *</Label>
                <Input
                  id="version"
                  placeholder="1.0.0"
                  value={uploadFormData.version}
                  onChange={(e) =>
                    setUploadFormData({
                      ...uploadFormData,
                      version: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="fileType">File Type *</Label>
                <Select
                  value={uploadFormData.fileType}
                  onValueChange={(value) =>
                    setUploadFormData({ ...uploadFormData, fileType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="installer">
                      Installer (Önerilen)
                    </SelectItem>
                    <SelectItem value="portable">Portable</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Platform: Windows
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="file">File *</Label>
                <Input
                  id="file"
                  type="file"
                  ref={fileInputRef}
                  accept=".exe,.zip"
                  onChange={handleFileChange}
                  required
                />
                {selectedFile && (
                  <p className="text-sm text-gray-500">
                    Seçili dosya: {selectedFile.name} (
                    {formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="minOsVersion">Minimum OS Version</Label>
                <Input
                  id="minOsVersion"
                  placeholder="Windows 10"
                  value={uploadFormData.minOsVersion}
                  onChange={(e) =>
                    setUploadFormData({
                      ...uploadFormData,
                      minOsVersion: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="releaseNotes">Release Notes</Label>
                <Textarea
                  id="releaseNotes"
                  placeholder="Bu versiyondaki yenilikler..."
                  value={uploadFormData.releaseNotes}
                  onChange={(e) =>
                    setUploadFormData({
                      ...uploadFormData,
                      releaseNotes: e.target.value,
                    })
                  }
                  rows={3}
                />
              </div>

              <div className="grid gap-2">
                <Label>Changelog</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Yeni değişiklik ekle..."
                    value={newChangelogItem}
                    onChange={(e) => setNewChangelogItem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddChangelogItem(true);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleAddChangelogItem(true)}
                  >
                    Ekle
                  </Button>
                </div>
                {uploadFormData.changelog.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {uploadFormData.changelog.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded"
                      >
                        <span>• {item}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveChangelogItem(index, true)}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="isLatest">Son Versiyon Olarak İşaretle</Label>
                <Switch
                  id="isLatest"
                  checked={uploadFormData.isLatest}
                  onCheckedChange={(checked) =>
                    setUploadFormData({ ...uploadFormData, isLatest: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseUploadDialog}
              >
                İptal
              </Button>
              <Button type="submit" disabled={uploadMutation.isLoading}>
                {uploadMutation.isLoading ? (
                  <>
                    <Upload className="mr-2 h-4 w-4 animate-pulse" />
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Yükle
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>Versiyonu Düzenle</DialogTitle>
              <DialogDescription>
                Versiyon bilgilerini güncelleyin
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-minOsVersion">Minimum OS Version</Label>
                <Input
                  id="edit-minOsVersion"
                  placeholder="macOS 10.15 / Windows 10"
                  value={editFormData.minOsVersion}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      minOsVersion: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-releaseNotes">Release Notes</Label>
                <Textarea
                  id="edit-releaseNotes"
                  placeholder="Bu versiyondaki yenilikler..."
                  value={editFormData.releaseNotes}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      releaseNotes: e.target.value,
                    })
                  }
                  rows={3}
                />
              </div>

              <div className="grid gap-2">
                <Label>Changelog</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Yeni değişiklik ekle..."
                    value={newChangelogItem}
                    onChange={(e) => setNewChangelogItem(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddChangelogItem(false);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleAddChangelogItem(false)}
                  >
                    Ekle
                  </Button>
                </div>
                {editFormData.changelog.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {editFormData.changelog.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded"
                      >
                        <span>• {item}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemoveChangelogItem(index, false)
                          }
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="edit-isLatest">
                  Son Versiyon Olarak İşaretle
                </Label>
                <Switch
                  id="edit-isLatest"
                  checked={editFormData.isLatest}
                  onCheckedChange={(checked) =>
                    setEditFormData({ ...editFormData, isLatest: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="edit-isActive">Aktif</Label>
                <Switch
                  id="edit-isActive"
                  checked={editFormData.isActive}
                  onCheckedChange={(checked) =>
                    setEditFormData({ ...editFormData, isActive: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseEditDialog}
              >
                İptal
              </Button>
              <Button type="submit" disabled={updateMutation.isLoading}>
                {updateMutation.isLoading ? "Güncelleniyor..." : "Güncelle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedVersion && (
                <>
                  <strong>{selectedVersion.version}</strong> versiyonunu silmek
                  üzeresiniz. Bu işlem geri alınamaz ve dosya Cloudinary'den de
                  silinecektir.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDeleteDialog}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? "Siliniyor..." : "Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
